import React, { useState, useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import Multiselect from 'multiselect-react-dropdown';
import SimpleReactValidator from 'simple-react-validator';
import FilterByArrow from './assets/dropDownArrow.svg';
import { CloseIcon } from 'app/assets/icons';
import { useTranslation } from 'react-i18next';
import { CommonMessages } from '../../../locales/CommonMessage/messages';
type Props = {
  data?: any[];
  preDataList?: any[];
  searchText?: string;
  label?: string;
  emptyRecordMsg?: string;
  validations?: any;
  type?: 'filterBy' | 'form-dropdown';
  showSearch?: boolean;
  onSelect?: (selectedList?: any, selectedItem?: any) => void;
  onRemove?: (selectedList: any, selectedItem: any) => void;
  validator?: SimpleReactValidator;
  className?: string;
  onClearSelectedItems?: () => void;
  singleSelect?: boolean;
};

const FilterMultiSelectDropdown = ({
  data,
  preDataList,
  searchText,
  type,
  label,
  emptyRecordMsg,
  validations,
  validator,
  showSearch,
  onSelect,
  onRemove,
  className,
  onClearSelectedItems,
  singleSelect,
}: Props) => {
  const { t } = useTranslation();
  const simpleValidator = useRef(validator);
  const multiselectRef = useRef<Multiselect | null>(null);
  const [show, setShow] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<any[]>(preDataList || []);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const convertedData = data?.map(item => ({
    text: item.name,
    id: item.id,
  }));

  const handleOnSelect = (selectedList: any, selectedItem: any) => {
    // For single select, prevent re-selecting the already selected item
    if (singleSelect && selectedItems.some(item => item.id === selectedItem.id)) {
      return;
    }

    const updatedSelectedItems = singleSelect
      ? [selectedItem]
      : [...selectedItems, selectedItem];
    setSelectedItems(updatedSelectedItems);
    onSelect?.(updatedSelectedItems, selectedItem);

    if (singleSelect) {
      setShow(false);
    }
  };

  const handleOnRemove = (selectedList: any, removedItem: any) => {
    if (singleSelect) {
      // Prevent removal of selected item from the dropdown list in single-select mode
      return;
    }
    const updatedSelectedItems = selectedItems.filter(
      item => item.id !== removedItem.id,
    );
    setSelectedItems(updatedSelectedItems);
    onRemove?.(updatedSelectedItems, removedItem);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target as Node)
    ) {
      setShow(false);
    }
  };

  const toggleDropdown = () => {
    setShow(!show);
  };

  const handleBlur = () => {
    if (validations) {
      simpleValidator?.current?.showMessageFor(label ? label : '');
    }
  };

  // clear selected items
  const clearSelectedItems = () => {
    // Completely clear selected items
    setSelectedItems([]);
    // If the Multiselect component is rendered, attempt to close the dropdown
    setShow(false);
    onClearSelectedItems?.();
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    setSelectedItems(preDataList || []);
  }, [preDataList]);

  return (
    <div className={className}>
      <MultiWrapper
        ref={wrapperRef}
        className={`MultiWrapper ${type ? type : ''} ${
          singleSelect ? 'single-select' : ''
        }`}
      >
        <MultiSelectWrapper className="MultiSelectWrapper">
          <StyledButton
            className={`drop-button-element ${show ? 'is-open' : ''}`}
            onClick={toggleDropdown}
            onBlur={handleBlur}
            $hasSelectedItems={selectedItems.length > 0}
          >
            {label && type === 'form-dropdown' && (
              <FloatLabel className="FloatLabel">
                {label}
                {validations && <span className="required-asterisk"> * </span>}
              </FloatLabel>
            )}

            {label && type === 'filterBy' ? (
              <>
                {label}
                {selectedItems.length > 0 && (
                  <SelectedCountWrapper>
                    <SelectedCount>{selectedItems.length}</SelectedCount>
                  </SelectedCountWrapper>
                )}
              </>
            ) : label && type === 'form-dropdown' ? (
              <>
                {selectedItems.length > 0 && (
                  <SelectedCountWrapper>
                    <SelectedCount>{selectedItems.length}</SelectedCount>
                  </SelectedCountWrapper>
                )}
              </>
            ) : (
              ''
            )}
          </StyledButton>
          {show && (
            <Multiselect
              className={`custom-multi-select ${
                showSearch ? '' : 'hide-search'
              }`}
              ref={multiselectRef}
              options={convertedData}
              selectedValues={selectedItems}
              onSelect={handleOnSelect}
              onRemove={handleOnRemove}
              displayValue="text"
              placeholder={searchText ? searchText : t(CommonMessages.search())}
              keepSearchTerm={showSearch}
              emptyRecordMsg={
                emptyRecordMsg ?? t(CommonMessages.noOptionsFound())
              }
              // disablePreSelectedValues=false so selected item stays in list with .selected class (for highlight)
              // hideSelectedList=true hides the chip/tag rendered above the list
              // pointer-events blocked via CSS on .selected li for single-select
              disablePreSelectedValues={false}
              hideSelectedList={true}
              showCheckbox={!singleSelect}
              avoidHighlightFirstOption
              optionValueDecorator={(value: string, option: any) => option.text}
            />
          )}
          {selectedItems.length > 0 && (
            <CloseIcon
              className="clear-button"
              color={theme.black}
              onClick={clearSelectedItems}
            />
          )}
        </MultiSelectWrapper>
        <ErrorText className="error-text">
          {simpleValidator?.current?.message(
            label ?? 'field',
            selectedItems,
            validations ?? '',
          )}
        </ErrorText>
      </MultiWrapper>
    </div>
  );
};

export default FilterMultiSelectDropdown;

export const MultiWrapper = styled.div`
  position: relative;
  display: grid;
  &.filterBy {
    .multiSelectContainer {
      min-width: 96px;
      top: 20px;
      border-radius: 6px;
      background: ${p => p.theme.white} !important;
      border: 1px solid ${p => p.theme.multiSelectContainerBorderClr};
      padding: 15px;
      padding-top: 0;
      padding-bottom: 0;
      margin: 10px 0 0 0;
      @media (min-width: 320px) and (max-width: 767px) {
        top: 30px;
      }
      input[type='checkbox'] {
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        display: inline-flex;
        align-content: center;
        justify-content: center;
        padding: 0;
        border: 1px solid ${p => p.theme.multiCheckBoxBorder};
        border-radius: 2px;
        margin: 0 10px 0 0;

        &::before {
          content: '';
          width: 14px;
          height: 14px;
          clip-path: polygon(
            28% 38%,
            41% 53%,
            75% 24%,
            86% 38%,
            40% 78%,
            15% 50%
          );
          transform: scale(0);
          /* background-color: ${p => p.theme.black}; */
        }
        &:checked {
          cursor: pointer;
          background-color: ${p => p.theme.checkBoxBackGroundColor} !important;
          border: 1.7px solid white;
          box-shadow: 0 0 0 0.5px ${p => p.theme.checkBoxBackGroundColor} !important;
          &::before {
            transform: scale(1.1);
          }
        }
      }
      .optionListContainer {
        position: relative;
        background: none;
        margin: 0 0 0 -15px;
        width: calc(100% - -30px);
        .optionContainer {
          border: none !important;
          overflow-x: hidden;
        }
        li {
          white-space: pre-line !important;
          display: flex !important;
          align-items: center !important;
          color: ${p => p.theme.black};
          background: ${p => p.theme.listBgClr} !important;
          font-size: 11px;
          font-weight: 400;
          font-family: 'open sans';
          padding: 10px 5px 10px 10px;
          display: flex;
          align-items: center;
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          width: 100%;
          display: block;
          &.highlightOption {
            background: transparent;
          }
          &.selected {
            background: ${p => p.theme.listBgClr};
            font-weight: 400;
          }
          &:hover {
            background: ${p => p.theme.listHoverClr} !important;
          }
        }
      }
    }
    .error-text {
      display: none;
    }
    button {
      height: 26px;
      background: ${p => p.theme.white};
      border-radius: 4px;
      color: ${p => p.theme.black};
      align-items: center;
      @media (min-width: 320px) and (max-width: 767px) {
        height: 40px;
      }
      &.is-open {
        &::before {
          transform: translate(-50%, -50%) rotate(180deg);
        }
      }
      &::before {
        content: '';
        position: absolute;

        background-size: contain;
        width: 10px;
        height: 10px;
      }
    }
  }
  &.form-dropdown {
    .multiSelectContainer {
      top: 20px;
      border-radius: 6px;
      background: ${p => p.theme.white} !important;
      border: 1px solid ${p => p.theme.multiSelectBorderClr};
      padding: 15px;
      margin: 10px 0 0 0;
      border-top: solid 1px ${p => p.theme.black} !important;
      input[type='checkbox'] {
        appearance: none;
        -webkit-appearance: none;
        display: inline-flex;
        align-content: center;
        justify-content: center;
        padding: 0;
        border: 1px solid ${p => p.theme.multiCheckBoxBorder};
        border-radius: 2px;
        margin: 0 10px 0 0;

        &::before {
          content: '';
          width: 14px;
          height: 14px;
          clip-path: polygon(
            28% 38%,
            41% 53%,
            75% 24%,
            86% 38%,
            40% 78%,
            15% 50%
          );
          transform: scale(0);
          background-color: ${p => p.theme.black};
        }
        &:checked {
          cursor: pointer;
          background-color: ${p => p.theme.checkBoxBackGroundColor} !important;
          border: 1.7px solid ${p => p.theme.white};
          box-shadow: 0 0 0 0.5px ${p => p.theme.checkBoxBackGroundColor} !important;
          &::before {
            transform: scale(1.1);
          }
        }
      }

      .optionListContainer {
        position: relative;
        background: none;
        margin: 0 0 0 -15px;
        padding: 10px 0 10px 0;
        width: calc(100% - -30px);
        .optionContainer {
          border: none !important;
        }
        li {
          white-space: pre-line !important;
          display: flex !important;
          align-items: center !important;
          color: ${p => p.theme.black};
          background: ${p => p.theme.listBgClr} !important;
          font-size: 11px;
          font-weight: 400;
          font-family: 'open sans';
          padding: 12px 25px;
          display: flex;
          align-items: center;
          &.highlightOption {
            background: transparent;
          }
          &.selected {
            background: ${p => p.theme.listBgClr};
            font-weight: 400;
          }
          &:hover {
            background: ${p => p.theme.listHoverClr} !important;
          }
        }
      }
    }
    button {
      border-radius: 5px;
      &.is-open {
        &::before {
          transform: translate(-50%, -50%) rotate(180deg);
        }
      }
      &::before {
        content: '';
        position: absolute;
      }
    }
  }

  /* Single-select specific overrides */
  &.single-select {
    .multiSelectContainer .optionListContainer {
      li {
        /* No auto-highlight on open */
        &.highlightOption {
          background: ${p => p.theme.listBgClr} !important;
          font-weight: 400;
        }
        /* Selected item: clearly visible highlight + block re-click */
        &.selected {
          background: ${p => p.theme.checkBoxBackGroundColor} !important;
          color: ${p => p.theme.white} !important;
          font-weight: 600 !important;
          pointer-events: none;
          cursor: default;
        }
        /* highlightOption + selected together */
        &.highlightOption.selected {
          background: ${p => p.theme.checkBoxBackGroundColor} !important;
          color: ${p => p.theme.white} !important;
          font-weight: 600 !important;
          pointer-events: none;
          cursor: default;
        }
        /* Non-selected items: normal hover */
        &:not(.selected):hover {
          background: ${p => p.theme.listHoverClr} !important;
          color: ${p => p.theme.black} !important;
        }
      }
    }
    /* Hide the chip/tag that multiselect renders for selected value */
    .search-wrapper .chip {
      display: none !important;
    }
  }

  button {
    margin: 0;
    width: 100%;
    height: 40px;
    border-radius: 4px;
    margin: 0px !important;
    font-family: 'open sans';
    padding: 0 10px 0 10px;
    font-size: 11px;
    width: 100%;
    text-align: left;
    > div {
      span {
        color: ${p => p.theme.dropDownRequiredColor};
      }
    }
    .showIcon {
      -webkit-justify-content: unset !important;
      justify-content: unset !important;
    }
    &::before {
      content: '';
      position: absolute;

      background-position: center;
      top: 50%;
      right: 5px;
      z-index: 2;
      font-family: 'open sans';
      width: 10px;
      height: 10px;
      transition: transform 0.5s ease;
      -webkit-transform: translate(-50%, -50%);
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
    }
    //Responsive
    @media (min-width: 100px) and (max-width: 319px) {
      font-size: 12px;
      height: 28px;
    }
    @media (min-width: 320px) and (max-width: 575px) {
      height: 28px;
    }
    @media (min-width: 576px) and (max-width: 767px) {
      height: 28px;
    }
    @media (min-width: 768px) and (max-width: 991px) {
      height: 28px;
    }
    @media (min-width: 992px) and (max-width: 1023px) {
      height: 28px;
    }
    @media (min-width: 1024px) and (max-width: 1279px) {
      height: 28px;
    }
    @media (min-width: 1280px) and (max-width: 1365px) {
      height: 32px;
    }
    @media (min-width: 1366px) and (max-width: 1439px) {
      height: 36px;
    }
    @media (min-width: 1440px) and (max-width: 1600px) {
      height: 38px;
    }
  }
  .clear-button {
    height: 100%;
    width: 10px;
    cursor: pointer;
    position: absolute;
    right: 7px;
    top: 1px;
  }
`;
export const MultiSelectWrapper = styled.div`
  position: relative;
  .custom-multi-select.hide-search .search-wrapper {
    display: none !important;
  }
  .search-wrapper {
    .chip {
      display: none !important;
    }
  }
  .multiSelectContainer {
    position: absolute;
    top: 35px;
    z-index: 10;
    .search-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      padding: 2px 2px;
      margin-top: 5px;
      border-radius: 4px;
      min-height: auto;
      background: ${p => p.theme.white} !important;
      border: solid 1px ${p => p.theme.multiSelectBorderClr} !important;
      &.searchWrapper {
        &::before {
          display: none !important;
        }
      }
      input {
        color: ${p => p.theme.dropDownLabelColor};
        margin: 0;
        width: 100%;
        font-size: 14px;
        min-width: 0;
      }
    }
  }
  .disableSelection {
    pointer-events: all;
    opacity: 1;
  }
  .optionListContainer {
    display: block !important;
    &.displayNone {
      display: block !important;
    }
    .optionContainer {
      &::-webkit-scrollbar {
        width: 3px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 10px;
      }
      &::-webkit-scrollbar-thumb {
        width: 9px;
        background: ${p => p.theme.dropDownMenuScrollThumbColor};
        border-radius: 10px;
      }
      //no data found label styles
      span {
        &.notFound {
          color: ${p => p.theme.black};
          padding: 10px 22px;
          font-size: 14px;
          font-family: 'open sans';
          text-align: center;
        }
      }
    }
  }
`;
export const StyledButton = styled.button<{ $hasSelectedItems?: boolean }>`
  color: ${p => p.theme.white};
  background: ${p => p.theme.listBgClr};
  border: 1px solid ${p => p.theme.multiSelectBorderClr};
  position: relative;
  cursor: pointer;

  &::before {
    display: ${props => (props.$hasSelectedItems ? 'none' : 'unset')};
    content: '';
    position: absolute;
    background-image: ${props =>
      props.$hasSelectedItems ? 'none' : `url(${FilterByArrow})`};
    background-position: center;
    top: 50%;
    right: 18px;
    z-index: 2;
    font-family: 'open sans';
    width: 10px;
    height: 10px;
    transition: transform 0.5s ease;
    transform: translate(-50%, -50%);
  }
`;

const ErrorText = styled.span`
  display: block;
  font-size: 13px;
  color: ${p => p.theme.dropDownRequiredColor};
  padding-top: 4px !important;
  text-align: left;
  font-family: 'open sans';
`;

const SelectedCountWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`;
const SelectedCount = styled.div`
  //Count value css
  padding: 1px 5px;
  background: ${p => p.theme.black};
  color: ${p => p.theme.white};
  margin: 0 4px;
  border-radius: 50%;
  width: fit-content;
  position: absolute;
  top: 52%;
  z-index: 0;
  transform: translateY(-50%);
  @media (min-width: 100px) and (max-width: 1279px) {
    padding: 1px 5px;
    font-size: 9px;
  }
  @media (min-width: 1279px) and (max-width: 1439px) {
    font-size: 10px;
    padding: 1px 5px;
  }
  span {
    color: ${p => p.theme.selectCountSpanClr};
  }
`;

const FloatLabel = styled.label`
  font-size: 12px;
  font-weight: 400;
  margin-left: 4px;
  font-family: 'Open Sans';
  color: ${p => p.theme.datePickerTitle};
  position: absolute;
  top: -25px;
  left: 1px;
  .required-asterisk {
    color: ${p => p.theme.dropDownRequiredColor};
    margin-left: 2px;
  }
  //Responsive
  @media (min-width: 100px) and (max-width: 319px) {
    font-size: 9px;
  }
  @media (min-width: 320px) and (max-width: 575px) {
    font-size: 9px;
  }
  @media (min-width: 576px) and (max-width: 767px) {
    font-size: 9px;
  }
  @media (min-width: 768px) and (max-width: 991px) {
    font-size: 9px;
  }
  @media (min-width: 992px) and (max-width: 1023px) {
    font-size: 9px;
    top: -7px;
  }
  @media (min-width: 1024px) and (max-width: 1279px) {
    font-size: 9px;
    top: -7px;
  }
  @media (min-width: 1280px) and (max-width: 1365px) {
    font-size: 10px;
    top: -8px;
  }
  @media (min-width: 1366px) and (max-width: 1439px) {
    font-size: 11px;
    top: -8px;
  }
  @media (min-width: 1440px) and (max-width: 1600px) {
    font-size: 12px;
    top: -8px;
  }
`;